import styled, { css } from 'styled-components';
import { Clickable } from 'src/components/clickable';
import { animations, layout } from 'src/components/base';
import { Truncated } from '../../../../../components/truncated';

export const HorizontalScrollPane = styled.div`
  overflow-x: auto;
  width: 100%;

  /**
   * This background specification is used to create fake "shadows" that appear on the left/right
   * sides when the pane is scrolled, and disappears when you reach the end of the pane. Uses a
   * variant of Lea Verou's method: https://lea.verou.me/2012/04/background-attachment-local/
   */
  background: linear-gradient(to right, #ffffff 33%, rgba(255, 255, 255, 0)),
    linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 66%) 100% 0,
    linear-gradient(to right, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)),
    linear-gradient(to left, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) 100% 0;
  background-color: #ffffff;
  background-repeat: no-repeat;
  background-attachment: local, local, scroll, scroll;
  background-size: 33px 100%, 33px 100%, 11px 100%, 11px 100%;
`;

type TableProps = {
  scrollable?: boolean;
};

export const Table = styled.table<TableProps>`
  ${props =>
    props.scrollable
      ? css`
          min-width: 100%;
        `
      : css`
          width: 100%;
        `}
  margin-bottom: ${layout.measurements.spacing150};
`;

export const SortIconContainer = styled.span<{ show: boolean }>`
  display: flex;
  align-items: center;
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
`;

const indicateNewColumnAnimation = animations.highlightNewContentAnimation();

// Track the horizontal padding used in the header cell in order to be able to set a better default
// minimum width for each column programmatically (see table-header.tsx)
const TH_HORIZONTAL_PADDING = layout.measurements.spacing050;
export const TH_TOTAL_HORIZONTAL_PADDING =
  parseFloat(TH_HORIZONTAL_PADDING) * 2;

type ThProps = {
  canBeSorted: boolean;
  indicateNewColumn?: boolean;
  sticky?: boolean;
  scrolled?: boolean;
  sidebarOpen?: boolean;
};
export const Th = styled.th<ThProps>`
  box-shadow: inset 0 -1px 0 ${({ theme }) => theme.lines.secondary};
  font-size: ${layout.measurements.fontSizeSmall};
  text-align: start;
  ${({ canBeSorted, theme }) =>
    canBeSorted &&
    css`
      &:hover,
      :focus {
        box-shadow: inset 0 -2px 0 ${theme.colors.yellove500};
        ${SortIconContainer} {
          visibility: visible;
        }
      }
    `}
  ${({ indicateNewColumn }) => indicateNewColumn && indicateNewColumnAnimation}
  ${p =>
    p.sticky &&
    css`
      position: sticky;
      top: ${layout.headerHeight.desktop.rem};
      left: calc(
        ${layout.sidebarMenuWidth.desktop.rem} +
          ${p.sidebarOpen ? layout.sidebarContentWidth.desktop.rem : '0px'}
      );
      background: ${p.theme.colors.base0};
      z-index: 6;

      &:first-of-type {
        z-index: 8;
      }

      &:after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: -1px;
        width: 30px;
        transform: translateX(100%);
        transition: box-shadow 0.3s;
        content: '';
        pointer-events: none;
        ${p.scrolled && 'box-shadow: inset 10px 0 8px -8px rgb(0, 0, 0, 0.15);'}
      }
    `}
`;

export const ClickableHeader = styled(Clickable)`
  display: block;
`;

export const HeaderTr = styled.tr`
  ${/* sc-selector */ Th}:first-child ${ClickableHeader} {
    padding-left: ${layout.measurements.spacing100};
  }
`;

export const HeaderText = styled.span`
  display: flex;
  padding: ${layout.measurements.spacing050};
  padding-left: ${TH_HORIZONTAL_PADDING};
  padding-right: ${TH_HORIZONTAL_PADDING};
  align-items: center;
`;

export const ColumnName = styled(Truncated)``;

/**
 * When the table is scrollable, the ad should stay in place independently of the table.
 */
export const StickyAdRow = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
`;

export const AdPositioningContext = styled.div`
  position: relative;
`;

export const AdTr = styled.tr``;

export const AdTd = styled.td`
  vertical-align: top;
`;
